import { Dispatch, SetStateAction } from "react";
import { Box, useTheme } from "@mui/material";
import { FoodsCheckbox, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { useConsumptionReference } from "src/api/sure-api/useConsumptionReference";
import { FoodsLoadingIndicator } from "src/components/EntityForm/FoodsFields";

type Step8ConsumptionProps = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
};

const CONSUMPTION_DISPLAY_ORDER = [
    "freezing",
    "oven",
    "microwave",
    "pan-cooking",
    "frying",
    "pan-warming",
    "refrigeration",
] as const;

/**
 * Step 8 - Consumption (Figma empty `12819:67587`, filled `13154:221896`, gated by `FS-product-consumption`).
 *
 * Direct multi-select checkbox grid (Figma displays all consumption references at
 * once, 2 columns). The previous v1 modal-based UX (`ConsumptionModal` opened from
 * `Page6`) is dropped here — `ProductFormV2` is a long-scroll form so all options
 * stay visible and toggling a checkbox immediately updates
 * `product.productConsumptions`.
 *
 * Existing entries (with backend `@id`/`id`) are preserved when their reference is
 * still selected, mirroring `Page6.onValidate` so a save round-trip doesn't drop
 * server-issued identifiers.
 */
export const Step8Consumption = (props: Step8ConsumptionProps) => {
    const { product, setProduct } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { consumptionReference, isLoading } = useConsumptionReference();

    if (isLoading) {
        return (
            <Box sx={{ padding: theme.spacing(3), display: "flex", justifyContent: "center" }}>
                <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
            </Box>
        );
    }

    if (consumptionReference === undefined) {
        return (
            <Box sx={{ padding: theme.spacing(3) }}>
                <Typography variant="body-regular" sx={{ color: theme.custom.grey[2000] }}>
                    {t("common.unexpected_error", "Unexpected error")}
                </Typography>
            </Box>
        );
    }

    if (consumptionReference.length === 0) {
        return (
            <Box sx={{ padding: theme.spacing(3) }}>
                <Typography variant="body-regular" sx={{ color: theme.custom.grey[2000] }}>
                    {t("product.consumption.no_methods_available", "No consumption modes available")}
                </Typography>
            </Box>
        );
    }

    const selectedRefIds = new Set(product.productConsumptions.map((pc) => pc.consumptionReferenceId));
    const consumptionOrderByCode = new Map<string, number>(
        CONSUMPTION_DISPLAY_ORDER.map((code, index) => [code, index]),
    );
    const orderedConsumptionReferences = consumptionReference
        .map((reference, originalIndex) => ({ reference, originalIndex }))
        .sort((left, right) => {
            const leftOrder = consumptionOrderByCode.get(left.reference.code);
            const rightOrder = consumptionOrderByCode.get(right.reference.code);

            if (leftOrder === undefined && rightOrder === undefined) {
                return left.originalIndex - right.originalIndex;
            }
            if (leftOrder === undefined) return 1;
            if (rightOrder === undefined) return -1;
            return leftOrder - rightOrder || left.originalIndex - right.originalIndex;
        })
        .map(({ reference }) => reference);
    const desktopRowCount = Math.ceil(orderedConsumptionReferences.length / 2);

    const toggleConsumption = (refId: number, checked: boolean) => {
        setProduct((prev) => {
            const alreadySelected = prev.productConsumptions.some((pc) => pc.consumptionReferenceId === refId);
            if (checked) {
                // Re-checking a box keeps the existing server `@id` identifier intact
                // because we never remove the entry on a redundant check.
                if (alreadySelected) return prev;
                return {
                    ...prev,
                    productConsumptions: [...prev.productConsumptions, { consumptionReferenceId: refId }],
                };
            }
            return {
                ...prev,
                productConsumptions: prev.productConsumptions.filter((pc) => pc.consumptionReferenceId !== refId),
            };
        });
    };

    return (
        <Box
            data-testid="consumption-options"
            role="group"
            aria-label={t("product.consumption.title", "Consumption")}
            sx={{
                padding: theme.spacing(3),
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gridTemplateRows: {
                    xs: "none",
                    md: `repeat(${desktopRowCount}, ${theme.spacing(2.75)})`,
                },
                gridAutoFlow: { xs: "row", md: "column" },
                rowGap: theme.spacing(1.5),
                columnGap: theme.spacing(4),
            }}
        >
            {orderedConsumptionReferences.map((ref) => (
                <FoodsCheckbox
                    key={ref.id}
                    values={[ref]}
                    selectedItems={selectedRefIds.has(ref.id) ? [ref] : []}
                    getId={(item) => item.id}
                    getName={(item) => item.label}
                    onChange={(selectedItems) => toggleConsumption(ref.id, selectedItems.length > 0)}
                    FormControlLabelProps={{
                        sx: {
                            margin: 0,
                            gap: theme.spacing(1.5),
                            minWidth: 0,
                            "& .MuiFormControlLabel-label": {
                                ...theme.typography["body-regular"],
                                color: theme.custom.grey[2500],
                            },
                        },
                    }}
                    CheckboxProps={{
                        sx: {
                            padding: 0,
                            flexShrink: 0,
                            "&.Mui-focusVisible": { outline: "auto" },
                        },
                    }}
                />
            ))}
        </Box>
    );
};
